// src/assetsApi.ts
// Thin client for /api/assets/* — see api/src/assets.ts. File and thumbnail
// bytes are fetched from each asset's same-origin fileUrl/thumbUrl, so
// <img>/<audio>/<video> elements just point at those directly.

import { apiFetch, ApiError } from './api';
import type { AssetKind, AssetSummary, AssetUpdate } from '@ring/shared/assets';

export type { AssetSummary, AssetUpdate };

export interface AssetList {
  assets:     AssetSummary[];
  /** Server clock at the time of the listing — pass back as `changedSince` to fetch only what changed. */
  serverTime: string;
}

export function listAssets(changedSince?: string): Promise<AssetList> {
  const query = changedSince ? `?changedSince=${encodeURIComponent(changedSince)}` : '';
  return apiFetch<AssetList>(`/api/assets${query}`, { method: 'GET' });
}

export interface UploadMetadata {
  kind?:      AssetKind;
  width?:     number;
  height?:    number;
  hasAlpha?:  boolean;
  durationS?: number;
}

/** Sends a raw body via XHR (fetch can't report upload progress) and parses the JSON reply like apiFetch does. */
function xhrPost<T>(url: string, body: Blob, onProgress?: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', body.type || 'application/octet-stream');
    if (onProgress) xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let data: Record<string, unknown> = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* non-JSON error page */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else reject(new ApiError(xhr.status, typeof data.error === 'string' ? data.error : `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new ApiError(0, 'Network error during upload.'));
    xhr.send(body);
  });
}

export function uploadAsset(file: File, meta: UploadMetadata, onProgress?: (fraction: number) => void): Promise<AssetSummary> {
  const params = new URLSearchParams({ filename: file.name });
  if (meta.kind)                    params.set('kind', meta.kind);
  if (meta.width)                   params.set('width', String(meta.width));
  if (meta.height)                  params.set('height', String(meta.height));
  if (meta.hasAlpha)                params.set('hasAlpha', 'true');
  if (meta.durationS)               params.set('durationS', String(meta.durationS));
  return xhrPost<AssetSummary>(`/api/assets?${params}`, file, onProgress);
}

export function uploadThumbnail(assetId: string, thumb: Blob): Promise<AssetSummary> {
  return xhrPost<AssetSummary>(`/api/assets/${assetId}/thumb`, thumb);
}

export function updateAsset(assetId: string, update: AssetUpdate): Promise<AssetSummary> {
  return apiFetch<AssetSummary>(`/api/assets/${assetId}`, { method: 'PATCH', body: JSON.stringify(update) });
}

export function deleteAsset(assetId: string): Promise<void> {
  return apiFetch<void>(`/api/assets/${assetId}`, { method: 'DELETE' });
}

/** Queues AI descriptions for these assets as one job (see api/src/jobs.ts). They come back 'pending' on the next poll. */
export function describeAssets(assetIds: string[]): Promise<unknown> {
  return apiFetch<unknown>('/api/jobs', { method: 'POST', body: JSON.stringify({ type: 'describe-assets', assetIds }) });
}

/** "Write it myself": takes the asset out of the AI queue so its description can be edited. */
export function cancelDescription(assetId: string): Promise<AssetSummary> {
  return apiFetch<AssetSummary>(`/api/assets/${assetId}/describe/cancel`, { method: 'POST' });
}
